import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { BarChart3, LifeBuoy, TrendingUp } from 'lucide-react';
import AdminBusinessMetrics from '../components/AdminBusinessMetrics';
import BusinessGrowthPanel from '../components/BusinessGrowthPanel';

// Time range selector options
type TimeRange = '7d' | '30d' | '90d';

const AdminAnalyticsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  // Only admin should access this page
  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="bg-red-50 dark:bg-red-500/10 p-4 rounded-md shadow border border-red-200 animate-fade-in-up">
          <div className="flex">
            <div className="flex-shrink-0">
              <LifeBuoy className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Acceso restringido</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>Solo los administradores pueden ver el panel de analíticas globales.</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-purple-600 dark:text-purple-400" />
              Panel de Analíticas Globales
            </h1>
            <p className="mt-1 text-sm text-content-2">
              Métricas y análisis de todos los negocios en la plataforma
            </p>
          </div>
          
          {/* Time range selector */}
          <div className="flex items-center bg-surface shadow-sm rounded-lg p-1 border border-line">
            {[
              { value: '7d', label: '7 días' },
              { value: '30d', label: '30 días' },
              { value: '90d', label: '90 días' }
            ].map((option) => (
              <button
                key={option.value}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  timeRange === option.value
                    ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300'
                    : 'text-content-2 hover:bg-canvas'
                }`}
                onClick={() => setTimeRange(option.value as TimeRange)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Panel de Crecimiento: catálogo y conversión de negocios (datos reales) */}
        <div className="animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          <BusinessGrowthPanel />
        </div>

        {/* Totales globales de la plataforma */}
        <div className="animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          <AdminBusinessMetrics />
        </div>

        {/* System Health Section */}
        <div className="bg-surface rounded-lg shadow p-6 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          <h2 className="text-lg font-medium text-content flex items-center mb-4">
            <TrendingUp className="h-5 w-5 mr-2 text-blue-500" />
            Estado del Sistema
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-green-50 dark:bg-green-500/10 p-4 rounded-lg border border-green-100">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-300">Elasticsearch</h3>
              <p className="mt-1 text-sm text-green-600 dark:text-green-400">Funcionando correctamente</p>
            </div>
            
            <div className="bg-green-50 dark:bg-green-500/10 p-4 rounded-lg border border-green-100">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-300">Firebase Firestore</h3>
              <p className="mt-1 text-sm text-green-600 dark:text-green-400">Funcionando correctamente</p>
            </div>
            
            <div className="bg-green-50 dark:bg-green-500/10 p-4 rounded-lg border border-green-100">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-300">Firebase Auth</h3>
              <p className="mt-1 text-sm text-green-600 dark:text-green-400">Funcionando correctamente</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminAnalyticsPage;